/**
 * Utility functions for selecting incremental vs cumulative rebate amounts
 *
 * Since backend now provides both totalRebateAmount (incremental) and
 * totalCumulativeRebateAmount (cumulative) on every RebatePoint, we can
 * simply select which field to use instead of transforming the data.
 */

import type { RebatePoint } from '@erp/shared-types';

/**
 * Get the rebate amount from a point based on whether cumulative view is enabled
 *
 * @param point - The rebate point
 * @param isCumulative - Whether to return cumulative amount (true) or incremental (false)
 * @returns The selected rebate amount
 */
export function getRebateAmount(
  point: RebatePoint,
  isCumulative: boolean,
): number {
  return isCumulative
    ? point.totalCumulativeRebateAmount
    : point.totalRebateAmount;
}

/**
 * Get the rebate amount from a point, with fallback for optional points
 *
 * @param point - The rebate point (may be null/undefined)
 * @param isCumulative - Whether to return cumulative amount (true) or incremental (false)
 * @param defaultValue - Default value if point is null/undefined (default: 0)
 * @returns The selected rebate amount or defaultValue
 */
export function getRebateAmountSafe(
  point: RebatePoint | null | undefined,
  isCumulative: boolean,
  defaultValue = 0,
): number {
  if (!point) return defaultValue;
  return getRebateAmount(point, isCumulative);
}
